import { Segmented } from 'antd'
import styled, { keyframes } from 'styled-components'
import { COLORS, SHADOWS } from 'tokens'

const flash = (bgColor: any) => keyframes`
  0% {
    background-color: ${bgColor};
    border: 1px solid transparent;
    color: #ffffff;
  }
  50% {
    background-color: var(--color-bg);
    border: 1px solid var(--color-disabled);
    color: #000000;
  }
  100% {
    background-color: ${bgColor};
    border: 1px solid transparent;
    color: #ffffff;
  }
`

// Metric tiles keep their own text colour, so they can't reuse flash() above.
const metricFlash = keyframes`
  0%, 100% {
    background-color: #FFCE00;
  }
  50% {
    background-color: var(--color-bg);
  }
`

export const Circle = styled.div`
  width: 33.8px;
  height: 33.8px;
  background-color: #007f7c;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const MiddleSection = styled.div`
  min-width: 614px;
  min-height: 851px;
  width: 40%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: var(--color-bg);
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  padding-top: 20px;
  padding-bottom: 20px;
`

export const TabSelector = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  background: var(--color-bg);
  border-radius: 6px;
  margin-bottom: 16px;
  justify-content: space-between;
`

export const HeaderLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 20px;
`

export const AtdBox = styled.div`
  height: 34px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  padding: 7px 9px;
`

export const MembershipExpiryDate = styled.div`
  width: 169px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-bg);
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  padding: 7px 9px;
`

// Owns the header's right margin so it lines up with MetricsSection, the same way
// HeaderLeftGroup owns the left. Children must not add their own right margins.
export const DayTagCircleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 20px;
`

export const DayTag = styled.div`
  display: flex;
  align-items: center;
  background: var(--color-bg);
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  height: 34px;
  overflow: hidden;
  box-shadow: ${SHADOWS.boxShadow};
`

export const DayTagLeft = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-weight: 700;
  font-size: 16px;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border-primary);
  height: 100%;
  width: 34px;
  min-width: 34px;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  text-align: center;
  line-height: 1;

  &:hover {
    background-color: ${(props) => (props.$disabled ? 'transparent' : 'var(--color-bg-hover, rgba(0, 0, 0, 0.02))')};
  }

  &:active {
    background-color: ${(props) => (props.$disabled ? 'transparent' : 'var(--color-bg-active, rgba(0, 0, 0, 0.05))')};
  }
`

export const DayTagRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-weight: 700;
  font-size: 16px;
  color: var(--color-text-primary);
  height: 100%;
  width: 115px;
  min-width: 115px;
  text-align: center;
  line-height: 1;
`

export const MetricsSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: transparent;
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  padding-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 8px;
  margin-left: 20px;
  margin-right: 20px;
  gap: 8px;
`

export const MetricsRow = styled.div`
  align-self: stretch;
  display: flex;
  align-items: flex-start;
  margin-left: 16px;
  margin-right: 16px;
`

export const MetricsColumn = styled.div`
  width: 58px;
  display: flex;
  flex-direction: column;
  margin-right: 8px;
  gap: 8px;
`

export const PainScaleBox = styled.div<{
  $bg?: string
  $border?: string
  $textColor?: string
  $animation?: boolean
  $disabled?: boolean
}>`
  width: 38px;
  height: 124px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bg || 'var(--color-bg)'};
  border-radius: 4px;
  border: ${(props) =>
    props.$disabled || props.$border ? `1px solid var(--color-disabled)` : `1px solid transparent`};
  padding-top: 56px;
  padding-bottom: 56px;
  padding-left: 14px;
  padding-right: 14px;
  margin-right: 2px;
  color: ${(props) => (props.$disabled || props.$textColor ? 'var(--color-disabled)' : 'var(--color-text-secondary)')};
  animation: ${(props) => (props.$animation ? flash(props.$bg) : 'none')} 1.5s infinite;
`

export const PainScaleText = styled.span<{ $color?: string; $animation?: boolean }>`
  font-size: 16px;
  font-weight: bold;
  color: inherit;
`

export const SymptomsSection = styled.div`
  height: 72px;
  width: calc(100% - 40px);
  display: flex;
  padding: 16px;
  margin-bottom: 8px;
  margin-left: 20px;
  margin-right: 20px;
  gap: 16px;
  border: 1px solid ${COLORS.borderPrimary} !important;
  border-radius: 6px;
`

export const SymptomsCard = styled.div`
  width: 124px;
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  font-size: 16px;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 16px;
  padding-right: 16px;
`

export const AppointmentsList = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  margin-left: 20px;
  margin-right: 20px;
`

export const SummaryNote = styled.div`
  width: calc(100% - 40px);
  min-height: 128px;
  max-height: 128px;
  overflow-y: auto;
  margin: 0 20px 8px;
  border-radius: 8px;
  border: 1px solid ${COLORS.borderPrimary};
  background: var(--color-bg);
  color: var(--color-text-primary);
  font-size: 16px;
  padding: 16px;
  resize: none;
  line-height: 1.4;
`

export const AppointmentRow = styled.div`
  height: 80px;
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: var(--color-bg);
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
`

export const AppointmentInfo = styled.div`
  width: 250px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  gap: 8px;
`

export const ScanTag = styled.div`
  height: 20px;
  width: 93px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
  border: 1px solid var(--color-border-primary);
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-bottom-left-radius: 6px;
  border-top: none;
  border-right: none;
  padding: 3px 15px 2px 14px;
  margin-left: 22px;
`

export const AppointmentTimeContainer = styled.div`
  width: 115px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`

export const StyledSegmented = styled(Segmented)<{ $disabled?: boolean }>`
  background: transparent !important;
  box-shadow: ${SHADOWS.boxShadow} !important;
  font-weight: 400;
  min-width: 136px;
  height: 34px;
  margin-right: 20px;
  border: 1px solid ${(props) => (props.$disabled ? COLORS.disabled : COLORS.borderPrimary)} !important;
  border-radius: 6px !important;
  padding: 0 !important;
  background: ${COLORS.background} !important;

  && .ant-segmented-thumb {
    background: ${COLORS.primary} !important;
    border-radius: 6px !important;
    transition: transform 0.1s ease-out !important;
    box-shadow: none !important;
  }

  .ant-segmented-item {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    border-radius: 6px !important;
    min-width: 68px !important;
    color: ${COLORS.textPrimary} !important;
    font-weight: 400 !important;
    transition: none !important;
  }

  .ant-segmented-item-selected {
    box-shadow: none !important;
    background: ${COLORS.primary} !important;
    color: ${COLORS.textSecondary} !important;
    min-width: 68px !important;
    font-weight: 700 !important;
    border-radius: 4px !important;
    transition: none !important;
  }

  .ant-segmented-item-disabled {
    background: transparent !important;
    color: ${(props) => (props.$disabled ? COLORS.disabled : COLORS.textPrimary)} !important;
    min-width: 68px !important;
    font-weight: 400 !important;
    border-radius: 6px !important;
    transition: none !important;
  }

  && .ant-segmented-item-selected {
    color: ${(props) => (props.$disabled ? COLORS.disabled : COLORS.textSecondary)} !important;
    font-weight: 700 !important;
  }

  .ant-segmented-item:first-child.ant-segmented-item-selected {
    border-right: 1px solid ${(props) => (props.$disabled ? COLORS.disabled : COLORS.borderPrimary)} !important;
  }

  .ant-segmented-item:last-child.ant-segmented-item-selected {
    border-left: 1px solid ${(props) => (props.$disabled ? COLORS.disabled : COLORS.borderPrimary)} !important;
  }
`

export const MetricButton = styled.div<{ $highlight?: boolean; $flash?: boolean }>`
  width: 58px;
  min-width: 58px;
  max-height: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${(props) => (props.$highlight ? '#FFCE00' : 'var(--color-bg)')};
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  padding-top: 3px;
  padding-left: 13px;
  padding-right: 13px;
  gap: 2px;
  text-align: left;
  animation: ${(props) => (props.$flash ? metricFlash : 'none')} 1.5s infinite;
`

export const MetricValue = styled.span`
  color: var(--color-text-primary);
  font-size: 30px;
  margin-bottom: 11px;
`

export const Text = styled.span<{
  $bold?: boolean
  $size?: number
  $color?: string
  $marginRight?: number
  $marginBottom?: number
  $marginTop?: number
}>`
  color: ${(props) => props.$color || 'var(--color-text-primary)'};
  font-size: ${(props) => props.$size || 16}px;
  font-weight: ${(props) => (props.$bold ? '700' : 'normal')};
  margin-right: ${(props) => (props.$marginRight ? `${props.$marginRight}px` : '0')};
  margin-bottom: ${(props) => (props.$marginBottom ? `${props.$marginBottom}px` : '0')};
  margin-top: ${(props) => (props.$marginTop ? `${props.$marginTop}px` : '0')};
`
